// Pass 2 — typeset the Sun. Each character cell reads one texel of pass 1:
// luminance picks a glyph from the density ramp (with a per-cell dither so the
// bands breathe), the glyph mask is sampled from the atlas, and the cell is
// coloured with the Sun's colour. Empty space stays black.
import { hash2 } from "@vgpu/wgsl-std/hash";

struct Params {
  grid: vec2f,
  glyphs: f32,
  time: f32,
}

@group(0) @binding(0) var<uniform> params: Params;
@group(0) @binding(1) var sun: texture_2d<f32>;
@group(0) @binding(2) var atlas: texture_2d<f32>;
@group(0) @binding(3) var nearest: sampler;
@group(0) @binding(4) var linear: sampler;

@fragment fn fs_main(@location(0) uv: vec2f) -> @location(0) vec4f {
  let cell = floor(uv * params.grid);
  let inCell = fract(uv * params.grid);
  let s = textureSampleLevel(sun, nearest, (cell + 0.5) / params.grid, 0.0);
  let h = hash2(cell + vec2f(0.37, 0.91));

  let lum = clamp(s.a, 0.0, 1.0);
  let color = s.rgb;
  let n = params.glyphs;
  var idx = floor(clamp(lum * (n - 1.0) + (h.y - 0.5) * 0.9 + 0.5, 0.0, n - 1.0));

  // the faint tail of the corona dithers out to black
  if (lum < 0.035 * (0.4 + h.x)) {
    idx = 0.0;
  }

  // Empty cell: skip the atlas fetch — most of the sky at noon. Output is
  // identical to sampling the blank space glyph (mask 0).
  if (idx < 0.5) {
    let bg = pow(max(color * 0.02 * lum, vec3f(0.0)), vec3f(1.0 / 2.2));
    return vec4f(bg, 1.0);
  }

  let mask = textureSampleLevel(atlas, linear, vec2f((idx + inCell.x) / n, inCell.y), 0.0).r;

  var out = color * mask * 1.15;
  out += color * 0.02 * lum; // a whisper of the cell's colour behind the glyph
  out = pow(max(out, vec3f(0.0)), vec3f(1.0 / 2.2));
  return vec4f(out, 1.0);
}
